/* ============================================================================
 * carterx-reviews.js
 * Dynamic Judge.me review-card renderer for the PDP "Reviews Section".
 *
 * Fetches reviews from the App Proxy (/a/account/api/reviews) and renders cards
 * whose DOM is byte-identical to the static theme-editor cards that used to live
 * in sections/reviews-section.liquid. All class names (incl. the per-section
 * `--<rev_id>` suffix) and the verified-badge mode are passed in from Liquid via
 * a `data-cls` JSON attribute on each [data-cx-reviews] container, so this file
 * never hard-codes the design — it mirrors whatever the section emits.
 *
 * Re-inits on shopify:section:load (Theme Editor wipes JS-injected DOM otherwise)
 * using a per-container `data-cx-init` guard.
 * ========================================================================== */
(function () {
  'use strict';

  var PROXY = '/a/account/api/reviews';
  var SUBMIT_ENDPOINT = PROXY + '/submit';

  // Expand/collapse model: show 6 cards by default (2 desktop rows of 3), reveal
  // STEP more per "See more" click, fetching the next page only when the already-
  // loaded set runs out. Once everything is shown the button flips to "See less".
  var DEFAULT_VISIBLE = 6;
  var STEP = 3;

  /* ---- utilities --------------------------------------------------------- */

  // HTML-escape arbitrary text via the DOM (reviews are user content → XSS).
  function esc(value) {
    var d = document.createElement('div');
    d.textContent = value == null ? '' : String(value);
    return d.innerHTML;
  }

  // Build the 5-glyph star row exactly like the Liquid loop:
  //   {%- for s in (1..5) -%}{%- if s <= star_count -%}★{%- else -%}☆{%- endif -%}{%- endfor -%}
  function starGlyphs(rating) {
    var r = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    var out = '';
    for (var s = 1; s <= 5; s++) out += s <= r ? '★' : '☆';
    return out;
  }

  // Whether the verified badge + "Verified Reviewer" label should render for a
  // given review, honouring the section's `verified_badge_mode` setting:
  //  - "verified_only" → only when the review itself is verified
  //  - "all" (default) → always (legacy behaviour)
  function showVerified(review, cls) {
    if (cls.badgeMode === 'verified_only') return review.verified === true;
    return true;
  }

  // The verified badge — mirrors the section's two modes:
  //  - cls.badgeImg present  → <img> using the merchant's uploaded badge
  //  - otherwise             → the inline SVG check-circle (default)
  function badgeMarkup(cls) {
    if (cls.badgeImg) {
      return (
        '<img class="reviews-section__card-badge ' + cls.badge + '"' +
        ' src="' + esc(cls.badgeImg) + '" alt="Verified" loading="lazy">'
      );
    }
    return (
      '<svg class="reviews-section__card-badge ' + cls.badge + '"' +
      ' viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<circle cx="6" cy="6.5" r="6" fill="#5BA8E0"></circle>' +
      '<path d="M3.5 6.5L5.5 8.5L8.5 4.5" stroke="white" stroke-width="1.5"' +
      ' stroke-linecap="round" stroke-linejoin="round"></path>' +
      '</svg>'
    );
  }

  // Photo / video thumbnails. Each opens the lightbox. (Renders nothing for
  // reviews with no media, so cards without pictures stay byte-identical.)
  function mediaMarkup(review, cls) {
    var pics = Array.isArray(review.pictures) ? review.pictures : [];
    var vids = Array.isArray(review.videos) ? review.videos : [];
    if (!pics.length && !vids.length) return '';

    var items = '';
    pics.forEach(function (url) {
      if (!url) return;
      items +=
        '<button type="button" class="cx-review-thumb ' + cls.thumb + '"' +
        ' data-cx-lightbox data-cx-type="image" data-cx-src="' + esc(url) + '"' +
        ' aria-label="View review photo">' +
        '<img src="' + esc(url) + '" alt="" loading="lazy"></button>';
    });
    vids.forEach(function (url) {
      if (!url) return;
      items +=
        '<button type="button" class="cx-review-thumb cx-review-thumb--video ' + cls.thumb + '"' +
        ' data-cx-lightbox data-cx-type="video" data-cx-src="' + esc(url) + '"' +
        ' aria-label="Play review video"><span class="cx-review-thumb__play">▶</span></button>';
    });
    return '<div class="cx-review-media ' + cls.media + '">' + items + '</div>';
  }

  // Optional country flag (sibling of card-body, matching the static markup).
  function flagMarkup(review, cls) {
    var src = review.flag || review.flagImage || review.countryFlag;
    if (!src) return '';
    return (
      '<div class="reviews-section__card-flag ' + cls.flag + '">' +
      '<img src="' + esc(src) + '" alt="" loading="lazy"></div>'
    );
  }

  // Full card. Inner structure mirrors the static Liquid exactly:
  //   card > card-body > (card-stars, card-title<p>, card-text<p>,
  //                       card-footer > (card-name<span>, badge, card-verified<span>))
  //        > card-flag (optional)
  // Media is appended after card-body (additive, optional). The "Helpful" vote
  // control was removed from the UI (the vote endpoint + ReviewVote table stay
  // server-side); no helpful markup is rendered here anymore.
  function cardHTML(review, cls) {
    var stars = starGlyphs(review.rating);
    var title = esc(review.title);
    var body = esc(review.body);
    var name = esc(review.name);

    // Gate badge + "Verified Reviewer" label together so they stay in lockstep.
    var verified = showVerified(review, cls)
      ? badgeMarkup(cls) +
        '<span class="reviews-section__card-verified ' + cls.verified + '">Verified Reviewer</span>'
      : '';

    var inner =
      '<div class="reviews-section__card-body ' + cls.body + '">' +
        '<div class="reviews-section__card-stars ' + cls.stars + '">' + stars + '</div>' +
        '<p class="reviews-section__card-title ' + cls.title + '">' + title + '</p>' +
        '<p class="reviews-section__card-text ' + cls.text + '">' + body + '</p>' +
        '<div class="reviews-section__card-footer ' + cls.footer + '">' +
          '<span class="reviews-section__card-name ' + cls.name + '">' + name + '</span>' +
          verified +
        '</div>' +
      '</div>';

    var card = document.createElement('div');
    card.className = 'reviews-section__card ' + cls.card;
    card.innerHTML = inner + flagMarkup(review, cls) + mediaMarkup(review, cls);
    return card;
  }

  /* ---- lightbox ---------------------------------------------------------- */

  var lightboxEl = null;

  function ensureLightbox() {
    if (lightboxEl) return lightboxEl;
    lightboxEl = document.createElement('div');
    lightboxEl.className = 'cx-lightbox';
    lightboxEl.setAttribute('hidden', '');
    lightboxEl.innerHTML =
      '<div class="cx-lightbox__backdrop" data-cx-lightbox-close></div>' +
      '<div class="cx-lightbox__inner" role="dialog" aria-modal="true">' +
      '<button type="button" class="cx-lightbox__close" data-cx-lightbox-close aria-label="Close">×</button>' +
      '<div class="cx-lightbox__stage" data-cx-lightbox-stage></div>' +
      '</div>';
    document.body.appendChild(lightboxEl);

    lightboxEl.addEventListener('click', function (e) {
      if (e.target.closest('[data-cx-lightbox-close]')) closeLightbox();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !lightboxEl.hasAttribute('hidden')) closeLightbox();
    });
    return lightboxEl;
  }

  function openLightbox(type, src) {
    var box = ensureLightbox();
    var stage = box.querySelector('[data-cx-lightbox-stage]');
    if (type === 'video') {
      stage.innerHTML =
        '<video src="' + esc(src) + '" controls autoplay playsinline class="cx-lightbox__media"></video>';
    } else {
      stage.innerHTML = '<img src="' + esc(src) + '" alt="" class="cx-lightbox__media">';
    }
    box.removeAttribute('hidden');
    document.documentElement.classList.add('cx-lightbox-open');
  }

  function closeLightbox() {
    if (!lightboxEl) return;
    lightboxEl.setAttribute('hidden', '');
    var stage = lightboxEl.querySelector('[data-cx-lightbox-stage]');
    if (stage) stage.innerHTML = ''; // stop any playing video
    document.documentElement.classList.remove('cx-lightbox-open');
  }

  /* ---- write-a-review modal --------------------------------------------- */

  // Single shared modal element (one per page, reused by every section).
  var writeEl = null;
  var writeCtx = null; // the [data-cx-reviews] container that opened it

  function ensureWriteModal() {
    if (writeEl) return writeEl;
    writeEl = document.createElement('div');
    writeEl.className = 'cx-review-modal';
    writeEl.setAttribute('hidden', '');
    writeEl.innerHTML =
      '<div class="cx-review-modal__backdrop" data-cx-write-close></div>' +
      '<div class="cx-review-modal__inner" role="dialog" aria-modal="true" aria-label="Write a review">' +
        '<button type="button" class="cx-review-modal__close" data-cx-write-close aria-label="Close">×</button>' +
        '<p class="cx-review-modal__eyebrow">Share your experience</p>' +
        '<h2 class="cx-review-modal__title">Write a review</h2>' +
        '<form class="cx-review-modal__form" data-cx-write-form novalidate>' +
          '<fieldset class="cx-review-modal__stars" data-cx-write-stars><legend class="cx-review-modal__label">Rating (required)</legend>' +
            [1, 2, 3, 4, 5].map(function (n) {
              return '<label class="cx-review-modal__star" data-star="' + n + '"><input type="radio" name="rating" value="' + n + '" aria-label="' + n + (n > 1 ? ' stars' : ' star') + '"><span aria-hidden="true">★</span></label>';
            }).join('') +
          '</fieldset>' +
          '<input class="cx-review-modal__hp" type="text" name="hp" tabindex="-1" autocomplete="off" aria-hidden="true">' +
          '<label class="cx-review-modal__label" for="cx-rv-name">Name</label>' +
          '<input class="cx-review-modal__input" id="cx-rv-name" name="name" type="text" maxlength="80" autocomplete="name" required>' +
          '<label class="cx-review-modal__label" for="cx-rv-email">Email</label>' +
          '<input class="cx-review-modal__input" id="cx-rv-email" name="email" type="email" maxlength="120" autocomplete="email" required>' +
          '<p class="cx-review-modal__hint">Your email is never published.</p>' +
          '<label class="cx-review-modal__label" for="cx-rv-title">Review title</label>' +
          '<input class="cx-review-modal__input" id="cx-rv-title" name="title" type="text" maxlength="120">' +
          '<label class="cx-review-modal__label" for="cx-rv-body">Review (required)</label>' +
          '<textarea class="cx-review-modal__textarea" id="cx-rv-body" name="body" rows="5" maxlength="5000" required></textarea>' +
          '<p class="cx-review-modal__msg" data-cx-write-msg hidden></p>' +
          '<button type="submit" class="cx-review-modal__submit" data-cx-write-submit>Submit review</button>' +
        '</form>' +
      '</div>';
    document.body.appendChild(writeEl);

    writeEl.addEventListener('click', function (e) {
      if (e.target.closest('[data-cx-write-close]')) closeWriteModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !writeEl.hasAttribute('hidden')) closeWriteModal();
    });
    var writeForm = writeEl.querySelector('[data-cx-write-form]');
    writeForm.addEventListener('submit', onWriteSubmit);
    writeForm.addEventListener('change', function (ev) {
      if (ev.target.name === 'rating') paintWriteStars(writeForm);
    });
    return writeEl;
  }

  function paintWriteStars(form) {
    var picked = form.querySelector('input[name="rating"]:checked');
    var v = picked ? Number(picked.value) : 0;
    var stars = form.querySelectorAll('[data-star]');
    for (var i = 0; i < stars.length; i++) {
      stars[i].classList.toggle('is-on', Number(stars[i].getAttribute('data-star')) <= v);
    }
  }

  function openWriteModal(ctx) {
    writeCtx = ctx;
    var box = ensureWriteModal();
    // Reset to a clean form each open (in case a prior submit disabled it).
    var form = box.querySelector('[data-cx-write-form]');
    form.reset();
    paintWriteStars(form);
    setFormDisabled(form, false);
    var submitBtn = form.querySelector('[data-cx-write-submit]');
    if (submitBtn) submitBtn.textContent = 'Submit review';
    showWriteMsg('', false);
    box.removeAttribute('hidden');
    document.documentElement.classList.add('cx-review-modal-open');
    var first = form.querySelector('input[name="rating"]');
    if (first) { try { first.focus(); } catch (e) {} }
  }

  function closeWriteModal() {
    if (!writeEl) return;
    writeEl.setAttribute('hidden', '');
    document.documentElement.classList.remove('cx-review-modal-open');
    writeCtx = null;
  }

  function setFormDisabled(form, disabled) {
    var els = form.querySelectorAll('input, select, textarea, button');
    for (var i = 0; i < els.length; i++) els[i].disabled = disabled;
  }

  // Show a status message. `ok === true` styles it as success, false as error,
  // and an empty text hides it. Text is escaped (server errors are echoed).
  function showWriteMsg(text, ok) {
    if (!writeEl) return;
    var el = writeEl.querySelector('[data-cx-write-msg]');
    if (!el) return;
    if (!text) { el.hidden = true; el.innerHTML = ''; return; }
    el.innerHTML = esc(text);
    el.className = 'cx-review-modal__msg ' + (ok ? 'is-ok' : 'is-error');
    el.hidden = false;
  }

  function onWriteSubmit(e) {
    e.preventDefault();
    var form = e.currentTarget;
    if (!writeCtx) { showWriteMsg('Something went wrong. Please refresh and try again.', false); return; }

    var productId = writeCtx.productId;
    var rating = Number((form.querySelector('input[name="rating"]:checked') || {}).value);
    var hp = ((form.querySelector('input[name="hp"]') || {}).value || '');
    var name = ((form.querySelector('#cx-rv-name') || {}).value || '').trim();
    var email = ((form.querySelector('#cx-rv-email') || {}).value || '').trim();
    var title = ((form.querySelector('#cx-rv-title') || {}).value || '').trim();
    var body = ((form.querySelector('#cx-rv-body') || {}).value || '').trim();

    // Client-side guards mirror the server's so we fail fast with a clear note.
    if (!rating) { showWriteMsg('Please choose a star rating.', false); return; }
    if (!name) { showWriteMsg('Please enter your name.', false); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { showWriteMsg('Please enter a valid email.', false); return; }
    if (body.length < 10) { showWriteMsg('Please write at least 10 characters.', false); return; }
    if (!productId) { showWriteMsg('Something went wrong. Please refresh and try again.', false); return; }

    setFormDisabled(form, true);
    var submitBtn = form.querySelector('[data-cx-write-submit]');
    if (submitBtn) submitBtn.textContent = 'Submitting…';
    showWriteMsg('', false);

    fetch(SUBMIT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        productId: productId, rating: rating, name: name,
        email: email, title: title, body: body, hp: hp
      })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (res.ok && res.data && res.data.ok) {
          // Success: lock the form and show the pending-approval confirmation.
          showWriteMsg(res.data.confirmEmail
            ? 'Thank you. Your review is being checked. We sent you an email: confirm it to get the verified buyer badge.'
            : 'Thank you. Your review will appear once it has been checked.', true);
          setFormDisabled(form, true);
          if (submitBtn) submitBtn.textContent = 'Submitted';
        } else {
          var err = (res.data && res.data.error) ? res.data.error : 'Could not submit your review. Please try again.';
          showWriteMsg(err, false);
          setFormDisabled(form, false);
          if (submitBtn) submitBtn.textContent = 'Submit review';
        }
      })
      .catch(function () {
        showWriteMsg('Network error. Please try again.', false);
        setFormDisabled(form, false);
        if (submitBtn) submitBtn.textContent = 'Submit review';
      });
  }

  /* ---- filtering + sorting (client-side) --------------------------------
   * V1 NOTE: reviews are fetched from the proxy in pages and accumulated in
   * ctx.reviews. The filter/sort controls operate ONLY over the pages loaded
   * so far (the in-memory set), not the full server-side review corpus. This
   * keeps the feature simple and zero-roundtrip; loading more pages then
   * re-applies the active filter to the larger set. */

  // Parse a review's timestamp for "Most recent" sorting. Falls back to 0 so
  // undated reviews sort last (stable-ish) rather than throwing.
  function reviewTime(rev) {
    var raw = rev.createdAt || rev.created_at || rev.date || rev.created || '';
    var t = raw ? Date.parse(raw) : NaN;
    return isNaN(t) ? 0 : t;
  }

  function hasPhotos(rev) {
    return Array.isArray(rev.pictures) && rev.pictures.length > 0;
  }

  // Apply the active filter + sort to ctx.reviews and return a new array.
  function applyFilters(ctx) {
    var f = ctx.filter;
    var out = ctx.reviews.filter(function (rev) {
      if (f.rating !== 'all') {
        if (Math.round(Number(rev.rating) || 0) !== Number(f.rating)) return false;
      }
      if (f.photos && !hasPhotos(rev)) return false;
      return true;
    });
    if (f.sort === 'highest') {
      out.sort(function (a, b) { return (Number(b.rating) || 0) - (Number(a.rating) || 0); });
    } else {
      // 'recent' (default) — newest first.
      out.sort(function (a, b) { return reviewTime(b) - reviewTime(a); });
    }
    return out;
  }

  // Whether any filter/sort is active (changes the default order/contents).
  function filtersActive(ctx) {
    var f = ctx.filter;
    return f.rating !== 'all' || f.photos || f.sort !== 'recent';
  }

  // Re-render the grid from the in-memory review set, honouring the filter, but
  // only paint the first ctx.visible cards (the expand/collapse window). Then sync
  // the See more / See less button to the total available in this filtered set.
  function renderGrid(ctx) {
    var list = filtersActive(ctx) ? applyFilters(ctx) : ctx.reviews;
    ctx.grid.innerHTML = '';
    var frag = document.createDocumentFragment();
    list.slice(0, ctx.visible).forEach(function (rev) { frag.appendChild(cardHTML(rev, ctx.cls)); });
    ctx.grid.appendChild(frag);
    updateMoreBtn(ctx, list.length);
  }

  // Own the See more / See less button's visibility + label/mode. `total` is the
  // count in the current filtered set.
  //  - more available (more loaded reviews to reveal, OR more pages to fetch)
  //    → "See more" in 'more' mode
  //  - else if we've expanded past the default → "See less" in 'less' mode
  //  - else (everything fits in the default window) → hide the button entirely
  function updateMoreBtn(ctx, total) {
    if (!ctx.moreBtn) return;
    var moreToShow = (ctx.visible < total) || !ctx.done;
    if (moreToShow) {
      ctx.moreBtn.hidden = false;
      ctx.moreBtn.style.display = '';
      ctx.moreBtn.textContent = ctx.moreLabel;
      ctx.moreBtn.setAttribute('data-cx-mode', 'more');
    } else if (ctx.visible > DEFAULT_VISIBLE) {
      ctx.moreBtn.hidden = false;
      ctx.moreBtn.style.display = '';
      ctx.moreBtn.textContent = ctx.lessLabel;
      ctx.moreBtn.setAttribute('data-cx-mode', 'less');
    } else {
      ctx.moreBtn.hidden = true;
      ctx.moreBtn.style.display = 'none';
    }
  }

  /* ---- fetch + render loop ---------------------------------------------- */

  function loadPage(ctx) {
    if (ctx.loading || ctx.done) return;
    ctx.loading = true;
    if (ctx.moreBtn) ctx.moreBtn.disabled = true;

    var params = [];
    if (ctx.scope) params.push('scope=' + encodeURIComponent(ctx.scope));
    if (ctx.handle) params.push('handle=' + encodeURIComponent(ctx.handle));
    params.push('page=' + ctx.page);
    var url = PROXY + '?' + params.join('&');
    console.log('[cx-reviews] loadPage fetch →', url);

    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        ctx.loading = false;
        if (!data) { fail(ctx); return; }

        var reviews = Array.isArray(data.reviews) ? data.reviews : [];
        console.log('[cx-reviews] response for', ctx.handle, '→', reviews.length, 'reviews');
        // Accumulate into the in-memory set, mark done state, then re-render.
        // renderGrid slices to ctx.visible and updateMoreBtn owns button
        // visibility/label, so no inline show/hide here anymore.
        for (var i = 0; i < reviews.length; i++) ctx.reviews.push(reviews[i]);

        ctx.rendered += reviews.length;
        ctx.page = (typeof data.page === 'number' ? data.page : ctx.page) + 1;

        var hasMore = !!data.hasMore;
        ctx.done = !hasMore;
        if (ctx.moreBtn) ctx.moreBtn.disabled = false;

        renderGrid(ctx);

        // Empty first page → hide the whole dynamic block so we don't show a
        // bare button / empty grid on products with no reviews.
        if (ctx.rendered === 0 && reviews.length === 0) {
          ctx.root.classList.add('cx-reviews--empty');
          if (ctx.moreBtn) { ctx.moreBtn.hidden = true; ctx.moreBtn.style.display = 'none'; }
        }
      })
      .catch(function () { ctx.loading = false; fail(ctx); });
  }

  function fail(ctx) {
    if (ctx.moreBtn) { ctx.moreBtn.disabled = false; }
    if (ctx.rendered === 0) ctx.root.classList.add('cx-reviews--error');
  }

  /* ---- init -------------------------------------------------------------- */

  function parseCls(root) {
    var raw = root.getAttribute('data-cls');
    var cls = {};
    if (raw) {
      try { cls = JSON.parse(raw); } catch (e) { cls = {}; }
    }
    // Defensive defaults so a malformed/missing attribute never throws.
    return {
      card: cls.card || '',
      body: cls.body || '',
      stars: cls.stars || '',
      title: cls.title || '',
      text: cls.text || '',
      name: cls.name || '',
      footer: cls.footer || '',
      verified: cls.verified || '',
      flag: cls.flag || '',
      media: cls.media || '',
      thumb: cls.thumb || '',
      badge: cls.badge || '',
      badgeImg: cls.badgeImg || '',
      badgeMode: cls.badgeMode || 'all'
    };
  }

  function initOne(root) {
    if (root.getAttribute('data-cx-init') === '1') return;
    root.setAttribute('data-cx-init', '1');

    var grid = root.querySelector('[data-cx-grid]');
    if (!grid) return;

    var ctx = {
      root: root,
      grid: grid,
      moreBtn: root.querySelector('[data-cx-more]'),
      filterToggle: root.querySelector('[data-cx-filter-toggle]'),
      filterBar: root.querySelector('[data-cx-filter-bar]'),
      cls: parseCls(root),
      handle: root.getAttribute('data-handle') || '',
      scope: root.getAttribute('data-scope') || '',
      productId: (root.dataset.productId || root.getAttribute('data-product-id') || '').trim(),
      page: 1,
      rendered: 0,
      loading: false,
      done: false,
      reviews: [],
      visible: DEFAULT_VISIBLE,
      filter: { rating: 'all', photos: false, sort: 'recent' }
    };

    // Capture the button labels AFTER ctx (so ctx.moreBtn is assigned). The
    // "more" label is whatever Liquid rendered into the button; the "less" label
    // comes from the button's data-less-label hook (with a safe fallback).
    ctx.moreLabel = (ctx.moreBtn && ctx.moreBtn.textContent.trim()) || 'See more reviews';
    ctx.lessLabel = (ctx.moreBtn && ctx.moreBtn.getAttribute('data-less-label')) || 'See less reviews';

    // The SEE MORE / SEE LESS button sits below the grid (centered) on desktop,
    // but on mobile it moves up next to FILTER (right-aligned, in the controls
    // row) so it balances the space above the carousel. One button, relocated by
    // viewport — no duplicate, so the toggle state stays in sync everywhere.
    var moreHomeDesktop = ctx.moreBtn ? ctx.moreBtn.parentNode : null;          // actions row, below grid
    var moreHomeMobile = ctx.filterToggle ? ctx.filterToggle.parentNode : null; // controls-buttons, beside FILTER
    function placeMoreBtn() {
      if (!ctx.moreBtn || !moreHomeDesktop || !moreHomeMobile) return;
      var target = window.matchMedia('(max-width: 990px)').matches ? moreHomeMobile : moreHomeDesktop;
      if (ctx.moreBtn.parentNode !== target) target.appendChild(ctx.moreBtn);
    }
    placeMoreBtn();
    window.addEventListener('resize', placeMoreBtn);

    // Read the current control values into ctx.filter, re-collapse to the default
    // window, and re-render.
    function syncFilter() {
      var ratingEl = root.querySelector('[data-cx-filter-rating]');
      var photosEl = root.querySelector('[data-cx-filter-photos]');
      var sortEl = root.querySelector('[data-cx-filter-sort]');
      ctx.filter.rating = ratingEl ? ratingEl.value : 'all';
      ctx.filter.photos = photosEl ? !!photosEl.checked : false;
      ctx.filter.sort = sortEl ? sortEl.value : 'recent';
      ctx.visible = DEFAULT_VISIBLE;
      renderGrid(ctx);
    }
    if (ctx.filterBar) {
      ctx.filterBar.addEventListener('change', syncFilter);
    }

    // Delegated handlers (survive appended cards).
    root.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-cx-lightbox]');
      if (thumb && root.contains(thumb)) {
        e.preventDefault();
        openLightbox(thumb.getAttribute('data-cx-type') || 'image', thumb.getAttribute('data-cx-src'));
        return;
      }

      var filterToggle = e.target.closest('[data-cx-filter-toggle]');
      if (filterToggle && root.contains(filterToggle)) {
        e.preventDefault();
        if (ctx.filterBar) {
          var open = ctx.filterBar.hasAttribute('hidden');
          if (open) { ctx.filterBar.removeAttribute('hidden'); }
          else { ctx.filterBar.setAttribute('hidden', ''); }
          filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
        return;
      }

      var write = e.target.closest('[data-cx-write]');
      if (write && root.contains(write)) {
        // Respect an explicit external write_url: if the section rendered the
        // button as a real <a href>, let it navigate (merchant opted out of the
        // in-page modal). Otherwise open our brand modal.
        var href = write.tagName === 'A' ? (write.getAttribute('href') || '').trim() : '';
        if (href && href !== '#') return;
        e.preventDefault();
        if (ctx.productId) openWriteModal(ctx);
        return;
      }

      var more = e.target.closest('[data-cx-more]');
      if (more && root.contains(more)) {
        e.preventDefault();
        if (more.getAttribute('data-cx-mode') === 'less') {
          // Collapse back to the default window and scroll the section back into
          // view so the user isn't stranded far down the page.
          ctx.visible = DEFAULT_VISIBLE;
          renderGrid(ctx);
          try { ctx.root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (err) {}
          return;
        }
        // "More": reveal STEP more cards, fetching the next page only when the
        // already-loaded set can't cover the new window.
        ctx.visible += STEP;
        if (ctx.visible > ctx.reviews.length && !ctx.done) loadPage(ctx);
        else renderGrid(ctx);
      }
    });

    root._cxCtx = ctx; // expose for the cx:product-switched reload (listener below)
    loadPage(ctx); // first page
  }

  function initAll(scope) {
    var containers = (scope || document).querySelectorAll('[data-cx-reviews]');
    for (var i = 0; i < containers.length; i++) initOne(containers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme Editor re-renders a section's DOM on every setting change, wiping our
  // injected cards. Re-init the affected section (the guard makes it idempotent
  // — the freshly re-rendered container has no data-cx-init attribute).
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target || document);
  });

  // In-page product switch: the product-box on dual-product PDPs (e.g. Hydration
  // vs Hydration +BCAAs) dispatches 'cx:product-switched'. Re-point every reviews
  // container at the newly selected product and reload its first page, reusing the
  // container's existing ctx + delegated listeners (no duplicate handlers).
  document.addEventListener('cx:product-switched', function (e) {
    var d = e.detail || {};
    var _dbg = document.querySelectorAll('[data-cx-reviews]');
    console.log('[cx-reviews] switch received → handle:', d.handle, '| containers:', _dbg.length, '| ctx on #0:', !!(_dbg[0] && _dbg[0]._cxCtx));
    if (!d.handle) return;
    var pid = (d.productId != null) ? String(d.productId) : '';
    var containers = document.querySelectorAll('[data-cx-reviews]');
    for (var i = 0; i < containers.length; i++) {
      var root = containers[i];
      root.setAttribute('data-handle', d.handle);
      if (pid) root.setAttribute('data-product-id', pid);
      var ctx = root._cxCtx;
      if (!ctx) { initOne(root); continue; } // not yet inited → fresh init
      ctx.handle = d.handle;
      if (pid) ctx.productId = pid;
      ctx.page = 1;
      ctx.reviews = [];
      ctx.done = false;
      ctx.loading = false;
      ctx.visible = DEFAULT_VISIBLE;
      ctx.rendered = 0;
      if (ctx.grid) ctx.grid.innerHTML = '';
      root.classList.remove('cx-reviews--empty', 'cx-reviews--error');
      if (ctx.moreBtn) { ctx.moreBtn.hidden = false; ctx.moreBtn.style.display = ''; }
      loadPage(ctx);
    }
  });
})();
